import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "react-datepicker/dist/react-datepicker.css";

import { getReportedAdsAction } from '../../../store/actions/property/PropertyActions';
import TableFooter from '../Common/TableFooter';
import NoDataFound from '../Common/NoDataFound';
import CommonExportButtons from '../Common/CommonExportButtons';
import ViewToggle from '../Common/ViewToggle';
import PageSizeSelector from '../Common/PageSizeSelector';
import ReportGridView from './reportListComponents/ReportGridView';
import ReportListView from './reportListComponents/ReportListView';

const ReportList = () => {
	const dispatch = useDispatch();
	const location = useLocation();

	const { reportedAds, loading } = useSelector((state) => state.property);
	const reportedAdsRows = reportedAds?.rows || [];
	const totalPages = reportedAds?.pages || 1;

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterQuery, setFilterQuery] = useState("");
    const [reasonFilter, setReasonFilter] = useState("");
    const [orderBy, setOrderBy] = useState('');
    const [orderByDir, setOrderByDir] = useState('desc');
    const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list'
    const [dateRange, setDateRange] = useState({
        startDate: null,
        endDate: null
    });	const buildSearchQuery = () => {
		const search = searchTerm ? searchTerm : '';
		const params = new URLSearchParams();
		if (search) {
			params.append('searchFields', 'comment');
			params.append('search', search);
		}
		if (reasonFilter) params.append('filter_reason', reasonFilter);
		if (dateRange.startDate && dateRange.endDate) {
			const startDateStr = dateRange.startDate.format('YYYY/MM/DD');
			const endDateStr = dateRange.endDate.format('YYYY/MM/DD');
			params.append('filter_createdAt', `btw-${startDateStr},${endDateStr}`);
		}
		if (orderBy) {
			params.append('orderBy', orderBy);
			params.append('orderByDir', orderByDir);
		}
		const queryString = `${params.toString()}`;
		return queryString;
	};

	const handleSort = (field) => {
		if (orderBy === field) {
			setOrderByDir(orderByDir === 'desc' ? 'asc' : 'desc');
		} else {
			setOrderBy(field);
			setOrderByDir('desc');
		}
		setCurrentPage(1);
	};

	const onSubmit = async () => {
		const query = buildSearchQuery();
		setFilterQuery(query);
		setCurrentPage(1);
	};

    const resetFilters = () => {
        setSearchTerm('');
        setReasonFilter('');
        setDateRange({ startDate: null, endDate: null });
        setOrderBy('');
        setOrderByDir('desc');
        setFilterQuery("");
        setCurrentPage(1);
    };

    const handleViewChange = (newViewMode) => {
        setViewMode(newViewMode);
    };    // Define export headers - matching table columns
    const exportHeaders = [
        { label: 'Property Title', key: 'propertyId.title' },
        { label: 'Reason', key: 'reason' },
        { label: 'Comment', key: 'comment' },
        { label: 'Reported By Name', key: 'userId.name' },
        { label: 'Reported By Phone', key: 'userId.phone' },
        { label: 'Reported By Email', key: 'userId.email' },
        { label: 'Reported Date', key: 'createdAt' }
    ];  useEffect(() => {
		const params = new URLSearchParams(location.search);
		const id = params.get('id');
		let query = 'populate=propertyId,userId';
		if (id) {
			query += `&filter__id=${id}`;
		}
		
		// Add search and filter parameters
		const searchQuery = buildSearchQuery();
		if (searchQuery) {
			query += `&${searchQuery}`;
		}
		
		let effectivePageSize = pageSize;
		if (pageSize === 100000) {
			query += '&noPagination=true';
			effectivePageSize = 100000;
		}
		
		dispatch(getReportedAdsAction(currentPage, effectivePageSize, '', query));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, currentPage, pageSize, location.search, filterQuery, orderBy, orderByDir]);


	return (
		<>
			<div className='form-head page-titles d-flex align-items-center'>
				<div className='me-auto d-lg-block'>
					<h4 className='mb-1'>Reported Ads List</h4>
				</div>
			</div>

			{/* Filter Section */}
			<div className="row">
				<div className="col-xl-12">
					<div className="card">
						<div className="card-body">
							<div className="row g-3">
								<div className="col-xl-3 col-sm-6">
									<label className="form-label">Search</label>
									<input
										type="text"
										className="form-control"
										placeholder="Search by comment"
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
									/>
								</div>
								<div className="col-xl-3 col-sm-6">
									<label className="form-label">Reason</label>
									<select
										className="form-control"
										value={reasonFilter}
										onChange={(e) => setReasonFilter(e.target.value)}
									>
										<option value="">All Reasons</option>
										<option value="Offensive content">Offensive content</option>
										<option value="fraud">Fraud</option>
										<option value="Duplicate ad">Duplicate ad</option>
										<option value="Product already sold">Product already sold</option>
										<option value="other">Other</option>
									</select>
								</div>
								<div className="col-xl-3 col-sm-6">
									<label className="form-label">Date Range</label>
									<DateRangePicker
										initialSettings={{
											...(dateRange.startDate && dateRange.endDate && {
												startDate: dateRange.startDate,
												endDate: dateRange.endDate,
											}),
											locale: {
												format: 'DD/MM/YYYY',
											},
											showDropdowns: true,
											autoUpdateInput: false,
										}}
										onApply={(event, picker) => {
											setDateRange({
												startDate: picker.startDate,
												endDate: picker.endDate,
											});
										}}
										onCancel={() => {
											setDateRange({ startDate: null, endDate: null });
										}}
									>
										<input
											type="text"
											className="form-control input-daterange-timepicker"
											placeholder="Select date range"
											readOnly
											value={
												dateRange.startDate && dateRange.endDate
													? `${dateRange.startDate.format('DD/MM/YYYY')} - ${dateRange.endDate.format('DD/MM/YYYY')}`
													: ''
											}
										/>
									</DateRangePicker>
								</div>
								<div className="col-xl-3 col-sm-6 d-flex align-items-end">
									<button
										type="button"
										className="btn btn-primary me-2"
										onClick={onSubmit}
									>
										<i className="fa fa-search me-1"></i>
										Search
									</button>
									<button
										type="button"
										className="btn btn-light"
										onClick={resetFilters}
										disabled={!(searchTerm || reasonFilter || dateRange.startDate || dateRange.endDate || orderBy)}
									>
										<i className="fa fa-refresh me-1"></i>
										Reset
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="row">
				<div className="col-12">
					<div className="d-flex mb-2 align-items-center justify-content-between">
						<PageSizeSelector 
							pageSize={pageSize}
							setPageSize={setPageSize}
							setCurrentPage={setCurrentPage}
							options={[10, 50, 100]}
						/>
						<div className="d-flex align-items-center gap-2">
							<CommonExportButtons 
								data={reportedAdsRows} 
								exportHeaders={exportHeaders}
								filename="reported-ads"
								pdfTitle="Reported Ads List"
								totalText="Total Reported Ads"
								pdfOrientation="landscape"
								pdfHeaderColor={[220, 53, 69]}
								includeTimestamp={true}
								dateFields={['property.createdAt']}
								columnWidths={{
									2: { cellWidth: 60 },
									3: { cellWidth: 50 },
								}}
							/>
							<ViewToggle viewMode={viewMode} onViewChange={handleViewChange} />
						</div>
					</div>

					<div className='col-xl-12'>
						<div>
							{(!loading && reportedAdsRows.length === 0) ? (
								<NoDataFound 
									message="No Reported Ads Found" 
									description="No reported ads match your current filters." 
								/>
							) : viewMode === 'grid' ? (
								<ReportGridView 
									reportedAdsRows={reportedAdsRows} 
									loading={loading} 
								/>
							) : (
								<ReportListView 
									reportedAdsRows={reportedAdsRows} 
									loading={loading}
									handleSort={handleSort}
									orderBy={orderBy}
									orderByDir={orderByDir}
								/>
							)}
						</div>
					</div>
				</div>
			</div>
			{(!loading && reportedAdsRows.length > 0) && (
				<TableFooter
					currentPage={currentPage}
					totalPages={totalPages}
					pageSize={pageSize}
					totalEntries={reportedAds?.total || reportedAdsRows.length}
				/>
			)}
		</>
	);
};

export default ReportList;
